/**
	@class ToggleCheck
	@classdesc checkboxとかradioとかselectでいろいろ切り替えるやつ
	@param {Object} op オプション
	@param {String} op.nameBase data-***-trigger、data-***、data-***-someの「***」の部分を指定
	@param {ToggleCheck#onMatch} op.onMatch 条件に一致したときに実行される関数
	@param {ToggleCheck#onUnmatch} op.onUnmatch 条件に一致しなかったときに実行される関数

@example <caption><b>HTML:文字色を切り替える</b></caption>
<!-- checkboxの場合 -->
<ul>
	<li><label><input type="checkbox" name="check" data-color-trigger="c1">check01</label></li>
	<li><label><input type="checkbox" name="check" data-color-trigger="c2">check02</label></li>
	<li><label><input type="checkbox" name="check" data-color-trigger="c3">check03</label></li>
	<li><label><input type="checkbox" name="check" data-color-trigger="c4">check04</label></li>
</ul>
<div data-color="c1">check01</div>
<div data-color="c2">check02</div>
<div data-color="c3">check03</div>
<div data-color="c4">check04</div>
<div data-color-some="c3, c4">all check</div><!--  ← c3 or c4どちらかにチェックが入ってる場合のみマッチ -->
<div data-color="c1, c2, c3, c4">all check</div><!--  ← c1, c2, c3, c4全てにチェックが入ってる場合のみマッチ -->

<!-- radioの場合 -->
<ul>
	<li><label><input type="radio" name="radio" data-color-trigger="r1">radio01</label></li>
	<li><label><input type="radio" name="radio" data-color-trigger="r2">radio02</label></li>
	<li><label><input type="radio" name="radio" data-color-trigger="r3">radio03</label></li>
	<li><label><input type="radio" name="radio" data-color-trigger="r4">radio04</label></li>
</ul>
<div data-color="r1">radio01</div>
<div data-color="r2">radio02</div>
<div data-color="r3">radio03</div>
<div data-color="r4">radio04</div>
<div data-color-some="r1, r2">radio01 or radio02</div><!--  ← r1 or r2どちらかにチェックが入ってる場合のみマッチ -->

<!-- selectの場合はoptionにdata-color-triggerを付ける -->
<select name="name" id="" class="mt20">
	<option value="" data-color-trigger="s1">option01</option>
	<option value="" data-color-trigger="s2">option02</option>
	<option value="" data-color-trigger="s3">option03</option>
	<option value="" data-color-trigger="s4">option04</option>
</select>
<div data-color="s1">option01</div>
<div data-color="s2">option02</div>
<div data-color="s3">option03</div>
<div data-color="s4">option04</div>

@example <caption><b>JS:文字色を切り替える</b></caption>
import {ToggleCheck} from '/assets/js/modules/ToggleCheck.js'
new ToggleCheck({
	nameBase: 'color',
	onMatch: (el) => el.css('color', '#f00'),
	onUnmatch: (el) => el.removeAttr('style'),
})

*/

/**
	条件に一致したときに実行される関数
	@callback ToggleCheck#onMatch
	@param {JQuery} el 条件に一致した要素
*/

/**
	条件に一致しなかったときに実行される関数
	@callback ToggleCheck#onUnmatch
	@param {JQuery} el 条件に一致しなかった要素
*/

export class ToggleCheck {
	constructor(op) {
		this.op = {
			nameBase: null,
			onMatch: null,
			onUnmatch: null,
			...op,
		}

		/**
			切り替えのトリガーになる要素
			@type {JQuery}
		*/
		this.$triggers = this.#getTriggers();

		/**
			切り替えの対象要素
			@type {JQuery}
		*/
		this.$targets = this.#getTargets();

		this.#toggle()
		this.$triggers.on('change', () => {
			this.#toggle()
		})
	}

	#toggle(){
		const checks = this.#collectChecks()

		this.$targets.some.each((i, e) => {
			let data = $(e).attr(`data-${this.op.nameBase}-some`)
			data = this.#toArr(data)
			const isSome = this.#isSome(data, checks)
			isSome
				? this.op.onMatch && this.op.onMatch($(e))
				: this.op.onUnmatch && this.op.onUnmatch($(e))
		})

		this.$targets.every.each((i, e) => {
			let data = $(e).attr(`data-${this.op.nameBase}`)
			data = this.#toArr(data)
			const isEvery = this.#isEvery(data, checks)
			isEvery
				? this.op.onMatch && this.op.onMatch($(e))
				: this.op.onUnmatch && this.op.onUnmatch($(e))
		})
	}

	#getTriggers(){
		let names = []
		$(`[data-${this.op.nameBase}-trigger]`).each(function(){
			if(this.localName === 'option') {
				names.push($(this).closest('select').attr('name'))
			} else {
				names.push($(this).attr('name'))
			}
		})
		names = [...new Set(names)]
		let triggers = names.map(n => $(`[name="${n}"]`))
		triggers = triggers.map(g => {
			const arr = []
			g.each((i, e) => {
				arr.push(e)
			})
			return arr
		}).flat()
		return $(triggers)
	}

	#getTargets(){
		return {
			some: $(`[data-${this.op.nameBase}-some]`),
			every: $(`[data-${this.op.nameBase}]`),
		}
	}

	#collectChecks(){
		const _this = this;
		let checks = []
		$(`[data-${this.op.nameBase}-trigger]:checked, [data-${this.op.nameBase}-trigger]:selected`).each(function(){
			const data = _this.#toArr($(this).attr(`data-${_this.op.nameBase}-trigger`))
			checks.push(data)
		})
		return [...new Set(checks.flat())]
	}

	#isSome(data, check) {
		return !!check.filter(v => data.includes(v)).length
	}

	#isEvery(data, check) {
		return check.filter(v => data.includes(v)).length == data.length
	}

	#toArr(str){
		return str.replace(/\s/g, '').split(',')
	}
}

/**
{@link ToggleCheck}を使ってdisabledを切り替え
@example <caption>JS</caption>
import {disabledToggle} from '/assets/js/modules/ToggleCheck.js'
disabledToggle();
*/
export const disabledToggle = () => {
	new ToggleCheck({
		nameBase: 'disabled',
		onMatch(el) {
			el.prop('disabled', false)
		},
		onUnmatch(el) {
			el.prop('disabled', true)
		},
	})
}

/**
{@link ToggleCheck}を使って表示/非表示を切り替え
@example <caption>JS</caption>
import {disabledToggle} from '/assets/js/modules/ToggleCheck.js'
disabledToggle();
*/
export const displayToggle = () => {
	new ToggleCheck({
		nameBase: 'display',
		onMatch(el) {
			el.removeClass('hidden')
		},
		onUnmatch(el) {
			el.addClass('hidden')
		},
	})
}